import { defaultPermissionsForRole, type AuthUser, type Role } from '@attendance/shared';
import { useAuthStore } from '../stores/authStore.js';

/**
 * "DOES THE SIGNED-IN PERSON HOLD THIS PERMISSION?"
 *
 * Reads the `permissions` list the server resolved onto the auth user (see
 * `AuthUserSchema`), so what Settings → Roles & access says is what the screens
 * do. An absent user answers `false` — never "allowed until proven otherwise".
 *
 * A user whose list is ABSENT (an older session, a fixture that names only a
 * `role`) holds what their base role holds out of the box, which is exactly what
 * the server would have resolved for an un-customised role. A list that is
 * PRESENT — even an empty one — is taken as given.
 *
 * This shows and hides; it does not protect. The API checks the same key on
 * every request.
 */

const DEFAULTS = new Map<Role, readonly string[]>();

/** What a base role holds out of the box, memoised: the catalog is static. */
export function defaultsFor(role: Role): readonly string[] {
  let held = DEFAULTS.get(role);
  if (!held) {
    held = defaultPermissionsForRole(role);
    DEFAULTS.set(role, held);
  }
  return held;
}

/** The permissions a user holds: their resolved list, else their role's defaults. */
export function heldPermissions(
  user: Pick<AuthUser, 'role' | 'permissions'> | null | undefined
): readonly string[] | undefined {
  if (!user) return undefined;
  return user.permissions ?? defaultsFor(user.role);
}

export function usePermission(key: string): boolean {
  return useAuthStore((s) => heldPermissions(s.user)?.includes(key) ?? false);
}

/** True when the person holds AT LEAST ONE of the keys. No keys → false. */
export function useCan(...keys: string[]): boolean {
  return useAuthStore((s) => {
    const held = heldPermissions(s.user);
    return !!held && keys.some((k) => held.includes(k));
  });
}

/** What one person may do to the records of one catalog module. */
export interface RecordAccess {
  create: boolean;
  edit: boolean;
  remove: boolean;
  /** May open a stored record's sheet at all: to change it, or to delete it. */
  open: boolean;
}

/**
 * `<module>.create` / `.edit` / `.delete`, read together — the keys a record
 * screen's Add, Save and Delete answer to, and the ones the API checks on POST,
 * PATCH and DELETE. Never the login role: a person whose access role is
 * narrower than their role column holds what the ACCESS ROLE says, and so does
 * the server (owner, 2026-10-21: "Add department" shown to a role without it).
 */
export function useRecordAccess(module: string): RecordAccess {
  const held = useAuthStore((s) => heldPermissions(s.user));
  const has = (action: string): boolean => !!held?.includes(`${module}.${action}`);
  const edit = has('edit');
  const remove = has('delete');
  return { create: has('create'), edit, remove, open: edit || remove };
}
